import {
  DuviSharedAdminCard as Card,
  DuviSharedAdminSwitch as Switch,
  DuviSharedAdminAvatar as Avatar,
  DuviSharedAdminBadge as Badge,
  DuviSharedAdminStatusPill as StatusPill,
  DuviSharedAdminIconButton as IconButton,
  DuviSharedAdminIcon as Icon,
  DuviSharedAdminTooltip as Tooltip,
  DuviSharedAdminDrawer as Drawer,
  DuviSharedAdminFormField as FormField,
  DuviSharedAdminInput as Input,
  DuviSharedAdminTextarea as Textarea,
  DuviSharedAdminButton as Button,
  DuviSharedAdminTestIds,
} from "@duvi-agency/ui";
import {
  DuviCoreAdminTable,
  DuviCoreAdminMenuPopover,
  DuviCoreAdminEmptyState as EmptyState,
  DuviCoreAdminShieldCheck as ShieldCheck,
  DuviCoreAdminShieldOff as ShieldOff,
  duviCoreAdminMenuItemCls,
} from "../../components/duvi-core-admin-kit";
import type { useDuviCoreAdminUserManagement } from "./use-duvi-core-admin-user-management";
import {
  type DuviCoreAdminMappedUser,
  duviCoreAdminUserStatusTone,
  duviCoreAdminUserStatusLabel,
} from "./use-duvi-core-admin-user-management";

type Row = DuviCoreAdminMappedUser & Record<string, unknown>;
type State = ReturnType<typeof useDuviCoreAdminUserManagement>;

const EMAIL_RE = /.+@.+\..+/;

export function DuviCoreAdminUserManagementView(state: State) {
  const {
    filteredUsers,
    showArchived,
    setShowArchived,
    showInvitePanel,
    inviteEmail,
    setInviteEmail,
    inviteNote,
    setInviteNote,
    inviteSent,
    sentEmail,
    inviteLoading,
    inviteError,
    handleSendInvite,
    handleInviteAnother,
    handleDone,
    handleDisable,
    handleEnable,
    handleArchive,
    handleUnarchive,
    handleForceReset,
    openUser,
  } = state;

  return (
    <div
      className="flex flex-col gap-4"
      data-testid={DuviSharedAdminTestIds.userManagementPage}
    >
      <div className="flex justify-end">
        <Switch
          label="Show archived"
          data-testid={DuviSharedAdminTestIds.userShowArchived}
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
        />
      </div>

      <Card>
        {filteredUsers.length === 0 ? (
          <EmptyState
            icon={<Icon name="users" size={22} />}
            title="No users found"
            desc="No platform admin accounts to show."
            testId={DuviSharedAdminTestIds.userEmpty}
          />
        ) : (
          <DuviCoreAdminTable<Row>
            rowKey="id"
            rowTestId={DuviSharedAdminTestIds.userRow}
            data={filteredUsers as Row[]}
            rowClassName={(u) =>
              u.status === "disabled" || u.status === "archived"
                ? "opacity-60"
                : undefined
            }
            columns={[
              {
                key: "name",
                header: "User",
                render: (u) => (
                  <span
                    className="flex items-center gap-2.5 cursor-pointer"
                    onClick={() => openUser(u.id)}
                  >
                    <Avatar name={u.name} src={u.avatarUrl} size="sm" />
                    <span>
                      <div className="flex items-center gap-1.5">
                        <b className="text-[13.5px]">{u.name}</b>
                        {u.isSelf && <Badge variant="accent">You</Badge>}
                      </div>
                      <div className="text-caption text-text-muted">
                        {u.email}
                      </div>
                    </span>
                  </span>
                ),
              },
              {
                key: "status",
                header: "Status",
                render: (u) => (
                  <StatusPill status={duviCoreAdminUserStatusTone(u.status)}>
                    {duviCoreAdminUserStatusLabel(u.status)}
                  </StatusPill>
                ),
              },
              {
                key: "mfa",
                header: "MFA",
                width: 70,
                render: (u) => (
                  <Tooltip
                    content={
                      u.mfaEnabled
                        ? "Two-factor enabled"
                        : "Two-factor not set up"
                    }
                  >
                    <span
                      className={[
                        "inline-flex",
                        u.mfaEnabled ? "text-success" : "text-text-muted",
                      ].join(" ")}
                    >
                      {u.mfaEnabled ? (
                        <ShieldCheck size={18} />
                      ) : (
                        <ShieldOff size={18} />
                      )}
                    </span>
                  </Tooltip>
                ),
              },
              {
                key: "lastLogin",
                header: "Last login",
                render: () => <span className="text-text-muted">—</span>,
              },
              {
                key: "actions",
                header: "",
                width: 52,
                align: "right",
                render: (u) => (
                  <DuviCoreAdminMenuPopover
                    align="right"
                    panelClassName="min-w-[210px]"
                    trigger={
                      <IconButton
                        label="User actions"
                        size="sm"
                        data-testid={DuviSharedAdminTestIds.userRowActions}
                      >
                        <Icon name="more" size={18} />
                      </IconButton>
                    }
                  >
                    <>
                      {u.status === "archived" ? (
                        <button
                          type="button"
                          className={duviCoreAdminMenuItemCls()}
                          data-testid={
                            DuviSharedAdminTestIds.userActionUnarchive
                          }
                          onClick={() => handleUnarchive(u.id)}
                        >
                          <Icon name="inbox" size={16} />
                          <span>Unarchive</span>
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className={duviCoreAdminMenuItemCls(
                              false,
                              u.isSelf || u.status === "invited",
                            )}
                            disabled={u.isSelf || u.status === "invited"}
                            data-testid={
                              DuviSharedAdminTestIds.userActionForceReset
                            }
                            onClick={() => handleForceReset(u.id)}
                          >
                            <Icon name="security" size={16} />
                            <span>Force reset password</span>
                          </button>
                          {u.status === "disabled" ? (
                            <button
                              type="button"
                              className={duviCoreAdminMenuItemCls(
                                false,
                                u.isSelf,
                              )}
                              disabled={u.isSelf}
                              data-testid={
                                DuviSharedAdminTestIds.userActionEnable
                              }
                              onClick={() => handleEnable(u.id)}
                            >
                              <Icon name="check" size={16} />
                              <span>Enable user</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={duviCoreAdminMenuItemCls(
                                false,
                                u.isSelf,
                              )}
                              disabled={u.isSelf}
                              data-testid={
                                DuviSharedAdminTestIds.userActionDisable
                              }
                              onClick={() => handleDisable(u.id)}
                            >
                              <Icon name="x" size={16} />
                              <span>Disable user</span>
                            </button>
                          )}
                          <div className="h-px bg-border mx-1 my-1.5" />
                          <button
                            type="button"
                            className={duviCoreAdminMenuItemCls(true, u.isSelf)}
                            disabled={u.isSelf}
                            data-testid={
                              DuviSharedAdminTestIds.userActionArchive
                            }
                            onClick={() => handleArchive(u.id)}
                          >
                            <Icon name="trash" size={16} />
                            <span>Archive</span>
                          </button>
                        </>
                      )}
                    </>
                  </DuviCoreAdminMenuPopover>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Drawer
        open={showInvitePanel}
        onClose={handleDone}
        className="max-w-[480px]"
        title={inviteSent ? "Invitation sent" : "Invite user"}
        footer={
          inviteSent ? (
            <Button block onClick={handleDone}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={handleDone}>
                Cancel
              </Button>
              <Button
                leftIcon={<Icon name="arrowUpRight" size={15} />}
                disabled={!EMAIL_RE.test(inviteEmail) || inviteLoading}
                onClick={handleSendInvite}
                data-testid={DuviSharedAdminTestIds.inviteUserSubmit}
              >
                {inviteLoading ? "Sending…" : "Send invite"}
              </Button>
            </>
          )
        }
      >
        {inviteSent ? (
          <div className="flex flex-col items-center text-center gap-3 py-5">
            <span className="w-14 h-14 rounded-full bg-success-subtle text-success inline-flex items-center justify-center">
              <Icon name="checkCircle" size={28} />
            </span>
            <div className="text-h4 font-semibold">Invitation on its way</div>
            <p className="m-0 text-text-secondary text-body-sm max-w-[300px]">
              {sentEmail} will receive a link to set a password and enrol MFA.
              They'll appear as <b>Invited</b> until they sign in.
            </p>
            <Button variant="ghost" size="sm" onClick={handleInviteAnother}>
              Invite another
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-[18px]">
            <FormField
              label="Email address"
              required
              help="They'll be added as a platform admin."
            >
              <Input
                type="email"
                placeholder="name@duvi.io"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid={DuviSharedAdminTestIds.inviteUserEmail}
              />
            </FormField>
            <FormField
              label="Note"
              help="Optional — included in the invite email."
            >
              <Textarea
                rows={4}
                placeholder="Add a short welcome note…"
                value={inviteNote}
                onChange={(e) => setInviteNote(e.target.value)}
              />
            </FormField>
            {inviteError && (
              <p className="m-0 text-body-sm text-error">
                {inviteError.message}
              </p>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
